import React, { useCallback, useState } from 'react';
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from 'react-hook-form';
import "../courseRatingReviewModal/courseRatingReviewModal.css"
import { addTrainerRatingReview } from '../../api/apiHandler';
import { showPromiseNotification } from '../../common/notification';
import { useTranslation } from 'react-i18next';
import '../../i18';

const TrainerRatingReviewModal = ({ isOpen, isClose, trainerId }) => {
    const { t } = useTranslation();
    const [rating, setRating] = useState(0);
    const [ratingError, setRatingError] = useState('');

    const ratingSchema = Yup.object().shape({
        review: Yup.string()
            .trim()
            .max(500, t('reviewValidation')),
    });

    const formOptions = { resolver: yupResolver(ratingSchema) };
    const { register, handleSubmit, formState, reset } = useForm(formOptions);
    const { errors } = formState;

    const onSubmit = useCallback(
        async (data) => {
            if (rating === 0) {
                setRatingError(t('ratingError'));
                return;
            }
            data.rating = rating;
            data.trainer_id = trainerId;
            const myPromise = new Promise(async (resolve, reject) => {
                await addTrainerRatingReview(data)
                    .then((response) => {
                        if (response.code == 200) {
                            resolve(response)
                        }
                        else {
                            reject(response)
                        }
                    });
            })
            showPromiseNotification(myPromise);
            // Reset form and rating after successful submission
            reset();
            setRating(0);
            setRatingError('');
            isClose();
        },
        [rating, reset, isClose]
    );

    if (!isOpen) return null;

    const handleStarClick = (value) => {
        setRating(value);
        setRatingError(''); // Clear the error message once a rating is selected
    };

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);

        // Render full stars
        for (let i = 1; i <= fullStars; i++) {
            stars.push(
                <i
                    key={`full-${i}`}
                    className="fa fa-star"
                    onClick={() => handleStarClick(i)}
                    style={{ cursor: 'pointer' }}
                />
            );
        }

        // Render remaining empty stars
        const emptyStarsCount = 5 - fullStars;
        for (let i = 1; i <= emptyStarsCount; i++) {
            stars.push(
                <i
                    key={`empty-${i + fullStars}`}
                    className="fa fa-star-o"
                    onClick={() => handleStarClick(fullStars + i)}
                    style={{ cursor: 'pointer' }}
                />
            );
        }

        return stars;
    };

    const handleModalClose = () => {
        setRating(0);
        setRatingError(''); // Clear error when closing modal
        isClose();
    };

    return (
        <div className="rating modal-overlay fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header border-bottom">
                        <h1 className="modal-title fs-5">
                            {t('ratingReviewModalHeading')}
                        </h1>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleModalClose}
                            aria-label="Close"
                        />
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="modal-body border-bottom">
                            <div className="ReviewOfModalMainBox">
                                <div className="ReviewOfModalBox UserNameStarBox">
                                    <div className="rating-inner">
                                        {renderStars(rating)}
                                    </div>
                                    {ratingError && <p className="text-danger">{ratingError}</p>}
                                    <div className="TextAreaBox single-input-inner m-0">
                                        <textarea
                                            name="review"
                                            placeholder={t("writeReview")}
                                            id="review"
                                            defaultValue={""}
                                            {...register("review")}
                                            style={{resize: 'none'}}
                                        />
                                    </div>
                                    <p className="text-danger">{errors.review?.message}</p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-center gap-4 border-0">
                            <button
                                type="button"
                                className="btn btn-border-black b-animate-3"
                                onClick={handleModalClose}
                            >
                                {t('cancel')}
                            </button>
                            <button className="btn btn-base b-animate-3">
                                {t('send')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default TrainerRatingReviewModal;